import React, { useEffect, useRef, useState } from "react";
import { Card } from "@mui/material";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useUI } from "context/LoadingContext";
import { BackImg } from "@utils/defaultImages";
import SteppedProgressBar from "@components/ui/ProgressBar";
import { useNotificationCount } from "@context/NotificationCountContext";

const GeneratingLoader = ({
  progressData,
  clearProgress,
  onComplete,
  onFail,
  slides,
  translationBase,
  progressBarConfig,
  recreateLoading = false,
  message,
  setMessage,
  stopCountdown,
  setTimeLeft,
}) => {
  const { t } = useTranslation();
  const { setIsActionLocked } = useUI();
  const [progress, setProgress] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { getCount } = useNotificationCount();
  const sliderRef = useRef();
  const videoRef = useRef();

  const pauseSlider = () => sliderRef.current?.slickPause();
  const playSlider = () => sliderRef.current?.slickPlay();

  // Logic: Lock UI actions
  useEffect(() => {
    setIsActionLocked(true);
    return () => setIsActionLocked(false);
  }, [setIsActionLocked]);

  // Logic: Handle Socket Progress
  useEffect(() => {
    if (!progressData) return;
    if (progressData.progress === 100) {
      setProgress(100);
      if (stopCountdown) stopCountdown();
      setTimeout(async () => {
        if (setTimeLeft) setTimeLeft(0);
        try {
          getCount();
          await onComplete();
          // Show toast only for meal plan as per original code logic
          if (translationBase.includes("meal")) {
            toast.info(t("toast.Your new Diet Plan created successfully!"));
          }
        } catch (err) {
          toast.error(t("Failed to load."));
        }
      }, 2000);
      clearProgress();
    }
    if (progressData.status === "in-progress")
      setMessage(progressData.progress);
    if (progressData.progress === 0 && progressData.status === "failed") {
      onFail();
      clearProgress();
    }
  }, [progressData]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !isVideoPlaying,
    autoplaySpeed: 10000,
    pauseOnHover: false,
    arrows: false,
    beforeChange: (current, next) => {
      setCurrentSlide(next);
      if (current === 0 && videoRef.current) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    },
    cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
    appendDots: (dots) => (
      <div>
        <ul
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "2px",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {React.Children.map(dots, (dot) =>
            React.cloneElement(dot, {
              style: { margin: 0, padding: 0, width: "12px", height: "12px" },
            }),
          )}
        </ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: i === currentSlide ? "#0575E6" : "#fff",
          transition: "background-color 0.3s ease",
        }}
      />
    ),
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    if (sliderRef.current) sliderRef.current.slickPause();
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
    if (sliderRef.current && currentSlide === 0) sliderRef.current.slickPlay();
  };

  return (
    <div className="flex justify-center items-start m-0">
      <Card
        className="px-6 pb-10 pt-6 md:p-6 md:pb-10 md:p-12 rounded-md w-full mb-5 bg-cover bg-center"
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        <SteppedProgressBar
          progress={progress}
          message={message}
          {...progressBarConfig}
        />

        <Slider
          ref={sliderRef}
          {...settings}
          className={`max-w-[80vw] md:max-w-[unset] ${
            recreateLoading && "!hidden"
          }`}
        >
          {slides.map((item, index) => (
            <div
              key={index}
              className="mt-[38px] rounded-lg !grid md:!grid-cols-2 min-h-[410px] xl:min-h-[458px] p-3 md:p-0"
              onMouseDown={pauseSlider}
              onMouseUp={playSlider}
              onMouseLeave={playSlider}
              onTouchStart={pauseSlider}
              onTouchEnd={playSlider}
            >
              {/* Media Section (RESTORED ORIGINAL DESIGN) */}
              <div className="flex w-full rounded-md overflow-hidden relative">
                <div className="w-[50%] hidden md:block"></div>
                {index === 0 ? (
                  <video
                    ref={videoRef}
                    className="object-contain bg-black md:transform md:-translate-y-1/2 rounded-md shadow-lg md:absolute md:left-[15%] md:top-[50%] w-full md:w-[80%] min-h-[240px] md:min-h-[300px] max-h-[371px]"
                    src={item}
                    playsInline
                    autoPlay
                    muted
                    controls
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onEnded={() => sliderRef.current?.slickNext()}
                  />
                ) : (
                  <img
                    className="rounded-md shadow-lg relative md:absolute md:left-[15%] top-[50%] w-full md:w-[80%] md:min-h-[300px] max-h-[371px] object-cover object-top"
                    style={{ transform: "translateY(-50%)" }}
                    src={item}
                    alt=""
                  />
                )}
              </div>

              {/* Text Content Section (RESTORED ORIGINAL DESIGN) */}
              <div className="flex items-center md:px-[20px]">
                <div className="w-full">
                  <h1 className="text-[16px] lg:text-[20px] xl:text-[28px] font-[700] text-[#fff] font-[Lato] mb-3 lg:mb-4 break-words hyphens-auto">
                    {t(`${translationBase}.slides.${index}.heading`)}
                  </h1>
                  <h2 className="text-[#fff] font-[400] font-[Lato] text-[12px] lg:text-[16px] xl:text-[20px] mb-3 lg:mb-6 break-words hyphens-auto">
                    {t(`${translationBase}.slides.${index}.subheading`)}
                  </h2>
                  <p className="text-[12px] xl:text-[18px] font-[400] text-[#fff] font-[Lato] break-words hyphens-auto leading-relaxed">
                    {t(`${translationBase}.slides.${index}.description`)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </Card>
    </div>
  );
};

export default GeneratingLoader;
